import { useQuery } from '@tanstack/react-query'
import { Database, HardDrive, Cpu, Zap } from 'lucide-react'
import { fetchApi, OrzForgeApiError } from '../lib/api'
import { clampPercent, formatBytes } from '../lib/utils'
import type { AccountProfile, DbTablesResponse, StorageResponse } from '../types'

function StatCard({
  icon: Icon,
  label,
  used,
  limit,
  unit,
}: {
  icon: React.ElementType
  label: string
  used: number
  limit: number
  unit?: string
}) {
  const pct = clampPercent(used, limit)
  return (
    <div className="rounded-xl border border-white/10 bg-panel p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/10 text-brand-400">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-gray-400">{label}</p>
          <p className="text-lg font-semibold">
            {unit === 'bytes' ? formatBytes(used) : used}
            <span className="ml-1 text-sm font-normal text-gray-500">
              / {unit === 'bytes' ? formatBytes(limit) : limit}
            </span>
          </p>
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full bg-brand-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-1.5 text-right text-xs text-gray-500">{pct}% usado</p>
    </div>
  )
}

export function OverviewPage() {
  const profileQuery = useQuery<AccountProfile>({
    queryKey: ['profile'],
    queryFn: async () => (await fetchApi('/v1/profile')) as AccountProfile,
    staleTime: 60_000,
  })
  const tablesQuery = useQuery<DbTablesResponse>({
    queryKey: ['db-tables'],
    queryFn: async () => (await fetchApi('/v1/db/tables')) as DbTablesResponse,
    staleTime: 60_000,
  })
  const storageQuery = useQuery<StorageResponse>({
    queryKey: ['storage'],
    queryFn: async () => (await fetchApi('/v1/storage/buckets')) as StorageResponse,
    staleTime: 60_000,
  })

  const isLoading = profileQuery.isLoading || tablesQuery.isLoading || storageQuery.isLoading
  const error = profileQuery.error ?? tablesQuery.error ?? storageQuery.error

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    const msg = error instanceof OrzForgeApiError ? error.message : 'Error desconocido'
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6 text-red-300">
        No se pudo cargar el resumen: {msg}
      </div>
    )
  }

  const profile = profileQuery.data!

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold">Resumen</h1>
        <p className="text-sm text-gray-400">
          Cuenta <span className="font-mono text-brand-300">{profile.account.id}</span> · Plan{' '}
          <span className="uppercase text-brand-300">{profile.account.plan}</span>
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Database}
          label="OrzDB"
          used={profile.usage.db_bytes}
          limit={profile.limits.db_bytes}
          unit="bytes"
        />
        <StatCard
          icon={HardDrive}
          label="OrzStorage"
          used={profile.usage.storage_bytes}
          limit={profile.limits.storage_bytes}
          unit="bytes"
        />
        <StatCard
          icon={Zap}
          label="AI requests / min"
          used={profile.usage.ai_requests_current_minute}
          limit={profile.limits.ai_requests_per_minute}
        />
        <StatCard
          icon={Cpu}
          label="CPU ms / min"
          used={profile.usage.compute_cpu_ms_current_minute}
          limit={profile.limits.compute_cpu_ms_per_minute}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-panel p-5 lg:col-span-2">
          <h2 className="mb-4 font-semibold">Actividad reciente</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-white/5 py-2">
              <span className="text-gray-400">Tokens AI acumulados</span>
              <span className="font-mono">{profile.usage.ai_tokens_total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between border-b border-white/5 py-2">
              <span className="text-gray-400">Índice vectorial</span>
              <span className="font-mono">
                {profile.usage.vector_dimensions_current_index} / {profile.limits.vector_dimensions}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-400">Tamaño máximo por archivo</span>
              <span className="font-mono">{formatBytes(profile.limits.file_bytes)}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-panel p-5">
            <h2 className="mb-3 font-semibold">OrzDB</h2>
            <p className="text-2xl font-bold">{tablesQuery.data?.tables.length ?? 0}</p>
            <p className="text-sm text-gray-500">tablas visibles</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-panel p-5">
            <h2 className="mb-3 font-semibold">OrzStorage</h2>
            <p className="text-2xl font-bold">{storageQuery.data?.files.length ?? 0}</p>
            <p className="text-sm text-gray-500">archivos listados</p>
          </div>
        </div>
      </div>

      {(profile.account.name || profile.account.email) && (
        <div className="rounded-xl border border-white/10 bg-panel p-5">
          <h2 className="mb-3 font-semibold">Perfil</h2>
          <div className="grid gap-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-gray-500">Nombre</p>
              <p>{profile.account.name ?? '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Email</p>
              <p>{profile.account.email ?? '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Creado</p>
              <p>{profile.account.created_at ?? '—'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
